import { FUNCTIONAL_TESTS } from "../content/tests";
import { WORKOUTS } from "../content/workouts";
import type { SkillTag, Workout } from "../content/types";
import type { AssessmentSummary } from "./scoring";

export interface ProgramDay {
  day: string;
  workoutSlug: string | null;
  label: string;
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const RETEST_INTERVAL_DAYS = 42;

function recommendedLevel(latestLevel: number | null): 1 | 2 | 3 {
  if (latestLevel === null || latestLevel <= 2) return 1;
  if (latestLevel === 3) return 2;
  return 3;
}

function pickWorkout(tag: SkillTag, level: number): Workout | null {
  const exact = WORKOUTS.find((w) => w.tags.includes(tag) && w.level === level);
  if (exact) return exact;
  const closest = WORKOUTS.filter((w) => w.tags.includes(tag)).sort((a, b) => a.level - b.level)[0];
  return closest ?? null;
}

function pickDailyRoutine(): Workout | null {
  return WORKOUTS.find((w) => w.tags.includes("Daily Routine") && w.level === 1) ?? WORKOUTS[0] ?? null;
}

// Builds a repeating weekly schedule targeting the user's lowest-scored tests.
// Monday/Tuesday/Thursday/Friday rotate through workouts matched to priority
// skill tags at a level appropriate to the user's current score; Wednesday and
// Saturday are an easy daily-routine session; Sunday is rest.
export function buildWeeklyProgram(summary: AssessmentSummary): ProgramDay[] {
  const priorityTests = summary.priorities
    .map((testId) => FUNCTIONAL_TESTS.find((t) => t.id === testId))
    .filter((t): t is (typeof FUNCTIONAL_TESTS)[number] => Boolean(t));

  const focusWorkouts = priorityTests
    .map((test) => {
      const scored = summary.perTest.find((p) => p.testId === test.id);
      return pickWorkout(test.targetSkill, recommendedLevel(scored?.latestLevel ?? null));
    })
    .filter((w): w is Workout => Boolean(w));

  const dailyRoutine = pickDailyRoutine();
  const rotation = focusWorkouts.length ? focusWorkouts : dailyRoutine ? [dailyRoutine] : [];

  const schedule: ProgramDay[] = [];
  let rotationIndex = 0;
  for (const day of DAYS) {
    if (day === "Sunday") {
      schedule.push({ day, workoutSlug: null, label: "Rest day" });
    } else if (day === "Wednesday" || day === "Saturday") {
      schedule.push({
        day,
        workoutSlug: dailyRoutine?.slug ?? null,
        label: dailyRoutine?.title ?? "Rest day",
      });
    } else if (rotation.length > 0) {
      const workout = rotation[rotationIndex % rotation.length]!;
      rotationIndex++;
      schedule.push({ day, workoutSlug: workout.slug, label: workout.title });
    } else {
      schedule.push({ day, workoutSlug: null, label: "Rest day" });
    }
  }
  return schedule;
}

export function nextRetestDate(lastSessionOn: string | null): string | null {
  if (!lastSessionOn) return null;
  const datePart = lastSessionOn.slice(0, 10);
  const [year, month, day] = datePart.split("-").map(Number) as [number, number, number];
  const base = new Date(Date.UTC(year, month - 1, day + RETEST_INTERVAL_DAYS));
  return base.toISOString().slice(0, 10);
}
